import { Link } from "react-router-dom";
import { PhoneCall, Clock, MapPin, CheckCircle } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import CallButton from "../components/CallButton";
import TrustBar from "../components/TrustBar";
import StaticMap from "../components/StaticMap";
import FinalCTABanner from "../components/FinalCTABanner";
import { BUSINESS, IMAGES } from "../data/constants";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact GTA Garage Doors | Get a Tech to Your Door"
        description="Need garage door repair or locksmith service in the GTA? Call us 24/7 - we respond in 15 minutes or less and dispatch a tech in under an hour."
        canonical="/contact"
        ogImage={IMAGES.heroTechnician}
      />

      <section className="relative bg-[#1B2A4A] text-white" data-testid="contact-hero">
        <div className="absolute inset-0 grain" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-20 md:py-28 text-center">
          <nav className="text-xs text-white/60 font-bold uppercase tracking-widest font-display">
            <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Contact</span>
          </nav>

          <div className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#FF6B00] px-3 py-1.5 text-xs font-bold font-display uppercase tracking-widest">
            <Clock size={14} weight="fill" /> Dispatching Right Now
          </div>

          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight max-w-4xl mx-auto leading-[1.02]">
            Don't type. <span className="text-[#FF6B00]">Tap to call.</span>
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            The fastest way to get help is the phone. A real tech answers in
            under 60 seconds, 24 hours a day.
          </p>

          <div className="mt-10 flex justify-center">
            <a
              href={BUSINESS.phoneHref}
              data-testid="contact-mega-call-button"
              className="inline-flex items-center gap-4 rounded-sm bg-[#FF6B00] text-white font-display font-black px-8 md:px-12 py-6 md:py-8 text-2xl md:text-4xl lg:text-5xl hover:bg-white hover:text-[#1B2A4A] transition-all hover:-translate-y-1 shadow-2xl"
            >
              <PhoneCall size={40} weight="fill" />
              <span className="phone-number">{BUSINESS.phone}</span>
            </a>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-4 md:gap-6 text-sm font-bold font-display uppercase tracking-widest text-white/80">
            <span className="inline-flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#FF6B00]" /> Real person, no bots</span>
            <span className="inline-flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#FF6B00]" /> 24/7, all 365 days</span>
            <span className="inline-flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#FF6B00]" /> Dispatch in under an hour</span>
          </div>
        </div>
      </section>

      <TrustBar variant="dark" />

      <section className="section-pad bg-white">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <div className="eyebrow">How it works</div>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight text-[#1B2A4A]">
              From your call to our truck in your driveway.
            </h2>
            <ol className="mt-8 space-y-5" data-testid="how-it-works">
              {[
                { n: "1", t: "You call", d: `Dial ${BUSINESS.phone}. A real dispatcher picks up — day or night.` },
                { n: "2", t: "We diagnose", d: "Quick 60-second chat. We tell you what's likely wrong and what parts we'll bring." },
                { n: "3", t: "We dispatch", d: "Nearest licensed tech heads to your address with the right parts on the truck." },
                { n: "4", t: "Fixed today", d: "Most GTA calls are fully repaired on the same visit, same day." },
              ].map((s) => (
                <li key={s.n} className="flex gap-5" data-testid={`step-${s.n}`}>
                  <div className="shrink-0 h-12 w-12 rounded-sm bg-[#1B2A4A] text-[#FF6B00] font-display font-black text-2xl flex items-center justify-center">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-display font-black text-xl text-[#1B2A4A]">{s.t}</div>
                    <p className="mt-1 text-[#4B5563] leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-10">
              <CallButton size="lg" testId="contact-main-call-button" />
            </div>
          </div>

          <aside className="rounded-sm bg-[#F8FAFC] border border-[#E2E8F0] p-6 md:p-10 space-y-6">
            <div>
              <div className="eyebrow">Emergency dispatch</div>
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-info-phone"
                className="mt-3 flex items-center gap-3 font-display font-black text-3xl md:text-4xl text-[#FF6B00] phone-number hover:underline"
              >
                <PhoneCall size={32} weight="fill" />
                {BUSINESS.phone}
              </a>
              <div className="mt-2 text-sm text-[#4B5563]">Tap to call from any mobile device.</div>
            </div>

            <div className="border-t border-[#E2E8F0] pt-6">
              <div className="eyebrow">Hours</div>
              <div className="mt-3 flex items-center gap-3 text-[#1B2A4A]">
                <Clock size={22} weight="fill" className="text-[#FF6B00]" />
                <div>
                  <div className="font-display font-bold">24 hours / 7 days</div>
                  <div className="text-sm text-[#4B5563]">Including weekends and holidays.</div>
                </div>
              </div>
            </div>

            <div className="border-t border-[#E2E8F0] pt-6">
              <div className="eyebrow">Coverage</div>
              <div className="mt-3 flex items-start gap-3 text-[#1B2A4A]">
                <MapPin size={22} weight="fill" className="text-[#FF6B00] shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold">Greater Toronto Area</div>
                  <div className="text-sm text-[#4B5563]">
                    Toronto, Vaughan, Mississauga, Brampton, Markham, Richmond Hill,
                    Thornhill, North York, Etobicoke, Scarborough, Oakville, Burlington.
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-[#E2E8F0] pt-6">
              <div className="eyebrow">Response note</div>
              <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">
                We respond to all inquiries within 15 minutes during business hours. For emergencies, call directly — that's always fastest.
              </p>
            </div>
          </aside>
        </div>

        <div className="container-wide mt-16">
          <StaticMap city="GTA Coverage" testId="contact-map" />
        </div>
      </section>

      <FinalCTABanner />
    </>
  );
}
